/**
 * Resolve the logged-in user's employee record for the current account.
 *
 * Some legacy accounts have a user employee linked to the account but not to
 * the Access row (`employee.access` is null). The access API then omits
 * `employee`, and Login.vue used to overwrite the nested employee from login.
 */

export function isValidEmployeeId(value) {
  if (value == null || value === "") return false;
  if (value === "undefined" || value === "null") return false;
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

export function resolveEmployeeFromUser(user, accountId) {
  if (!user) {
    return null;
  }

  const accountIdNum = Number(accountId);
  const employeeFromUser = user.employee;

  if (employeeFromUser?.id != null) {
    const employeeAccount = employeeFromUser.account;
    if (employeeAccount == null || Number(employeeAccount) === accountIdNum) {
      return employeeFromUser;
    }
  }

  if (user.employee_id != null) {
    return { id: user.employee_id };
  }

  return null;
}

export function resolveAuthEmployee({
  user = null,
  accessPermission = null,
  accountId = null,
  routeEmployeeId = null,
} = {}) {
  if (isValidEmployeeId(routeEmployeeId)) {
    return { id: Number(routeEmployeeId) };
  }

  if (accessPermission?.employee?.id != null) {
    return accessPermission.employee;
  }

  const fromUser = resolveEmployeeFromUser(user, accountId);
  if (fromUser?.id != null) {
    return fromUser;
  }

  return null;
}

export function resolveAuthEmployeeId(options = {}) {
  return resolveAuthEmployee(options)?.id ?? null;
}

export function parseStoredAuthContext() {
  let user = null;
  let accessPermission = null;
  let accountId = null;

  try {
    user = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    user = null;
  }

  try {
    accessPermission = JSON.parse(localStorage.getItem("accessPermission") || "null");
  } catch {
    accessPermission = null;
  }

  accountId = localStorage.getItem("currentAccountID");

  return { user, accessPermission, accountId };
}

export function resolveStoredAuthEmployeeId(routeEmployeeId = null) {
  const { user, accessPermission, accountId } = parseStoredAuthContext();
  return resolveAuthEmployeeId({
    user,
    accessPermission,
    accountId,
    routeEmployeeId,
  });
}

/** Keep localStorage user.employee aligned with the best available source. */
export function syncUserEmployeeInStorage({ user, accessPermission, accountId }) {
  if (!user?.id) {
    return user;
  }

  const employee = resolveAuthEmployee({ user, accessPermission, accountId });
  const updatedUser = {
    ...user,
    employee: employee ?? null,
  };

  localStorage.setItem("user", JSON.stringify(updatedUser));

  if (employee?.id != null) {
    localStorage.setItem("emp_id", String(employee.id));
  }

  return updatedUser;
}
